import { useQueryParams } from "@certn/api";
import { HelpButton, useAuth, useLogout } from "@certn/auth";
import {
  DropdownMenu,
  GhostIconButton,
  MediaQuery,
  MenuItem,
  MenuSection,
  PrimaryButton,
  PrimaryTopNav,
  ProfileDropdown,
  Separator,
  Space,
  TopNavRegionToggle,
} from "@certn/cascadia-react";
import {
  EVENT_NAMES,
  parseName,
  showPendoGuideById,
  useEventTracking,
  useTimeTracking,
} from "@certn/utils";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useEntitledRegions } from "../../hooks/useEntitledRegions";
import { useUser } from "../../hooks/useUser";
import {
  clearOrderDraft,
  useOrderDraftKey,
} from "../../pages/order/orderDraftStorage";
import { usePermissions } from "../permissions/PermissionsProvider";
import { SelectPackageModal } from "../select-package-modal/SelectPackageModal";
import { AppLogo } from "./AppLogo";

export function AppHeader() {
  const { user } = useAuth();
  const { logout } = useLogout();
  const { t } = useTranslation();
  const { details: userDetails } = useUser();
  const orderDraftKey = useOrderDraftKey();
  const navigate = useNavigate();
  const [isPackageModalOpen, setIsPackageModalOpen] = useState<boolean>(false);
  const { canSwitchRegion } = useEntitledRegions();
  const {
    clientPortalGuidedTourDropdown,
    clientPortalNewOrderingFlow,
    clientPortalPackageManagement,
  } = useFlags();
  const { hasPermission } = usePermissions();
  const { params } = useQueryParams();
  const userFromGroupPage = !!params.customer_group_id;
  const guidedTour = () => {
    navigate("/");
    // launch pendo tour
    showPendoGuideById("5PqULG4kTM7e4QBeROoQ8f_SUvY");
  };
  const { startTimeTracker } = useTimeTracking();
  const { trackEvent } = useEventTracking();

  const userCanOrder =
    hasPermission({
      action: "create",
      target: "case",
      scope: "all",
    }) ||
    hasPermission({
      action: "create",
      target: "case",
      scope: "my_groups",
    });

  return (
    <PrimaryTopNav
      testID="app-header"
      logo={<AppLogo />}
      onLogoPress={() => {
        navigate("/");
      }}
    >
      {canSwitchRegion && (
        <MediaQuery type="hide" sm md>
          <TopNavRegionToggle />
        </MediaQuery>
      )}
      <Space />
      <HelpButton />
      {!clientPortalNewOrderingFlow &&
        userDetails?.client_account?.auth0_id &&
        userCanOrder && (
          <>
            <MediaQuery sm md type="hide">
              <PrimaryButton
                testID="header-large-screen-new-order-button"
                onPress={() => {
                  clearOrderDraft(orderDraftKey);
                  startTimeTracker("order--order-complete");

                  if (userFromGroupPage)
                    navigate(
                      `/order/?customer_group_id=${params.customer_group_id}`
                    );
                  else navigate("/order/");
                }}
                className="client-portal--app-header--new-order-click-rate"
              >
                {t("New order")}
              </PrimaryButton>
            </MediaQuery>
            <MediaQuery lg xl type="hide">
              <GhostIconButton
                icon="add"
                testID="header-small-medium-screen-new-order-button"
                aria-label={t("New order")}
                onPress={() => {
                  clearOrderDraft(orderDraftKey);
                  startTimeTracker("order--order-complete");
                  if (userFromGroupPage)
                    navigate(
                      `/order?customer_group_id=${params.customer_group_id}`
                    );
                  else navigate("/order/");
                }}
                className="client-portal--app-header--new-order-click-rate"
              />
            </MediaQuery>
          </>
        )}
      {clientPortalNewOrderingFlow &&
        userDetails?.client_account?.auth0_id &&
        userCanOrder && (
          <DropdownMenu
            minWidth="220px"
            placement="bottom right"
            triggerButton={
              <>
                <MediaQuery sm md type="hide">
                  <PrimaryButton
                    variant="brand-primary"
                    testID="header-large-screen-new-order-button"
                    className="client-portal--app-header--new-order-click-rate"
                  >
                    {t("New order")}
                  </PrimaryButton>
                </MediaQuery>
                <MediaQuery lg xl type="hide">
                  <GhostIconButton
                    icon="add"
                    testID="header-small-medium-screen-new-order-button"
                    aria-label={t("New order")}
                  />
                </MediaQuery>
              </>
            }
            onItemAction={(menuItem) => {
              clearOrderDraft(orderDraftKey);
              switch (menuItem) {
                case "custom-order":
                  trackEvent(EVENT_NAMES.CP_INITIATED_ORDER, {
                    orderType: "custom",
                  });
                  startTimeTracker("order--order-complete");
                  if (userFromGroupPage)
                    navigate(
                      `/order?customer_group_id=${params.customer_group_id}&step=1`
                    );
                  else navigate("/order?step=1");
                  break;
                case "select-package":
                  trackEvent(EVENT_NAMES.CP_INITIATED_ORDER, {
                    orderType: "package",
                  });
                  setIsPackageModalOpen(true);
                  break;
              }
            }}
          >
            {clientPortalPackageManagement && (
              <MenuItem
                icon="package_2"
                id="select-package"
                testID="select-package-button"
              >
                {t("Select package")}
              </MenuItem>
            )}
            <MenuItem
              icon="quick_reference_all"
              id="custom-order"
              testID="custom-order-button"
            >
              {t("Build your own")}
            </MenuItem>
          </DropdownMenu>
        )}
      {user && (
        <ProfileDropdown
          name={parseName(user.name)}
          email={user.email}
          onItemAction={(id) => {
            switch (id) {
              case "guided-tour":
                guidedTour();
                break;
              case "sign-out":
                logout();
                break;
            }
          }}
          testID="app-header-profile-dropdown"
        >
          {clientPortalGuidedTourDropdown && (
            <>
              <MenuSection>
                <MenuItem id="guided-tour" testID="pendo-guided-tour">
                  {t("Guided tour")}
                </MenuItem>
              </MenuSection>
              <Separator />
            </>
          )}
          <MenuSection>
            <MenuItem id="sign-out" testID="sign-out-menu-item">
              {t("Sign out")}
            </MenuItem>
          </MenuSection>
        </ProfileDropdown>
      )}
      {isPackageModalOpen && (
        <SelectPackageModal setIsOpen={setIsPackageModalOpen} />
      )}
    </PrimaryTopNav>
  );
}
